import React, { useState, useCallback, useEffect, useRef } from 'react';

interface EntityNavigatorProps {
  onEntitySelect: (entity: any) => void;
  selectedAOI?: any;
}

const getAuthToken = (): string | null =>
  localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');

const ENTITY_TYPES = [
  { key: 'VESSEL', label: 'Vessel', color: 'bg-blue-600' },
  { key: 'AIRCRAFT', label: 'Aircraft', color: 'bg-cyan-600' },
  { key: 'PERSON', label: 'Person', color: 'bg-orange-500' },
  { key: 'ORGANIZATION', label: 'Org', color: 'bg-purple-600' },
  { key: 'LOCATION', label: 'Location', color: 'bg-green-600' },
  { key: 'EVENT', label: 'Event', color: 'bg-red-600' },
  { key: 'ASSET', label: 'Asset', color: 'bg-yellow-600' },
];

const TYPE_ICONS: Record<string, string> = {
  VESSEL: '\u2693',     // anchor
  AIRCRAFT: '\u2708',   // airplane
  PERSON: '\u263A',     // person
  ORGANIZATION: '\u2302', // building
  LOCATION: '\u2316',   // pin
  EVENT: '\u26A0',      // warning
  ASSET: '\u2B23',      // hexagon
};

const PRIORITY_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: 'P1', color: 'text-red-400' },
  2: { label: 'P2', color: 'text-orange-400' },
  3: { label: 'P3', color: 'text-yellow-400' },
  4: { label: 'P4', color: 'text-green-400' },
  5: { label: 'P5', color: 'text-gray-400' },
};

/** Compute centroid and approximate radius_km from an AOI geometry */
function computeAoiFilter(aoi: any): { lat: number; lon: number; radius_km: number } | null {
  if (!aoi?.geometry) return null;
  const geom = aoi.geometry;
  let coords: number[][] = [];
  if (geom.type === 'Point') {
    return { lat: geom.coordinates[1], lon: geom.coordinates[0], radius_km: 50 };
  } else if (geom.type === 'Polygon') {
    coords = geom.coordinates?.[0] || [];
  } else if (geom.type === 'MultiPolygon') {
    coords = geom.coordinates?.[0]?.[0] || [];
  }
  if (coords.length === 0) return null;
  const lat = coords.reduce((a, c) => a + c[1], 0) / coords.length;
  const lon = coords.reduce((a, c) => a + c[0], 0) / coords.length;
  // Approximate radius as half the diagonal of the bounding box, in km
  const lats = coords.map(c => c[1]);
  const lons = coords.map(c => c[0]);
  const dlat = (Math.max(...lats) - Math.min(...lats)) * 111;
  const dlon = (Math.max(...lons) - Math.min(...lons)) * 111 * Math.cos(lat * Math.PI / 180);
  const radius_km = Math.max(Math.sqrt(dlat * dlat + dlon * dlon) / 2, 10);
  return { lat, lon, radius_km: Math.ceil(radius_km) };
}

const EntityNavigator: React.FC<EntityNavigatorProps> = ({ onEntitySelect, selectedAOI }) => {
  const [query, setQuery] = useState('');
  const [activeTypes, setActiveTypes] = useState<string[]>([]);
  const [showWatchlist, setShowWatchlist] = useState(false);
  const [scopeToAOI, setScopeToAOI] = useState(true);
  const [entities, setEntities] = useState<any[]>([]);
  const [watchlistEntities, setWatchlistEntities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<any>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Fetch entity stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = getAuthToken();
        const resp = await fetch('/api/entities/stats', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (resp.ok) setStats(await resp.json());
      } catch { /* ignore */ }
    };
    fetchStats();
  }, []);

  // Fetch watchlist
  const fetchWatchlist = useCallback(async () => {
    try {
      const token = getAuthToken();
      const resp = await fetch('/api/entities/watchlist?size=50', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        setWatchlistEntities(data.entities || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (showWatchlist) fetchWatchlist();
  }, [showWatchlist, fetchWatchlist]);

  // Search entities with debounce
  const searchEntities = useCallback(async (searchQuery: string, types: string[], aoiFilter: ReturnType<typeof computeAoiFilter>) => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const body: any = {
        query: searchQuery || '*',
        size: 100,
        offset: 0,
      };
      if (types.length > 0) body.entity_types = types;
      // Scope to AOI geographic area when an AOI is selected
      if (aoiFilter) {
        body.location = { lat: aoiFilter.lat, lon: aoiFilter.lon };
        body.radius_km = aoiFilter.radius_km;
      }

      const resp = await fetch('/api/entities/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        const data = await resp.json();
        setEntities(data.entities || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Entity search failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search — re-runs when AOI selection or scope toggle changes
  useEffect(() => {
    if (showWatchlist) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const aoiFilter = scopeToAOI ? computeAoiFilter(selectedAOI) : null;
    debounceRef.current = setTimeout(() => {
      searchEntities(query, activeTypes, aoiFilter);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, activeTypes, showWatchlist, selectedAOI, scopeToAOI, searchEntities]);

  const toggleType = (type: string) => {
    setActiveTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const displayEntities = showWatchlist ? watchlistEntities : entities;

  const formatTime = (ts: string) => {
    if (!ts) return 'N/A';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffH = Math.floor(diffMs / 3600000);
    if (diffH < 1) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffH < 24) return `${diffH}h ago`;
    return `${Math.floor(diffH / 24)}d ago`;
  };

  return (
    <div className="space-y-3">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Entity Navigator</h3>
        {stats && (
          <span className="text-[10px] text-muted-foreground">
            {stats.entities || stats.total_entities || 0} entities
          </span>
        )}
      </div>

      {/* Search bar */}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search entities..."
          className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm pr-8"
        />
        {loading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-cyan-400 border-t-transparent rounded-full" />
          </div>
        )}
      </div>

      {/* Type filters */}
      <div className="flex flex-wrap gap-1">
        {ENTITY_TYPES.map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => toggleType(key)}
            className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
              activeTypes.includes(key)
                ? `${color} text-white`
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {TYPE_ICONS[key]} {label}
          </button>
        ))}
      </div>

      {/* Watchlist + AOI scope toggles */}
      <div className="flex items-center gap-2 border-t border-border pt-2">
        <button
          onClick={() => setShowWatchlist(!showWatchlist)}
          className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
            showWatchlist
              ? 'bg-amber-600 text-white'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          Watchlist {watchlistEntities.length > 0 && `(${watchlistEntities.length})`}
        </button>
        {selectedAOI && (
          <button
            onClick={() => setScopeToAOI(!scopeToAOI)}
            title={scopeToAOI ? 'Showing entities within this AOI — click to show all' : 'Click to scope to selected AOI'}
            className={`px-2 py-1.5 rounded text-[10px] font-medium transition-colors ${
              scopeToAOI
                ? 'bg-cyan-600 text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {scopeToAOI ? `AOI` : 'All'}
          </button>
        )}
        {!showWatchlist && (
          <span className="text-[10px] text-muted-foreground">
            {total} results
          </span>
        )}
      </div>

      {/* Entity list */}
      <div className="space-y-1 max-h-[400px] overflow-y-auto">
        {displayEntities.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-4">
            {loading ? 'Searching...' : showWatchlist ? 'No watchlisted entities' : 'No entities found'}
          </div>
        ) : (
          displayEntities.map((entity: any) => {
            const entityType = entity.entity_type || 'UNKNOWN';
            const typeConfig = ENTITY_TYPES.find(t => t.key === entityType);
            const priority = entity.priority || 3;
            const priorityConfig = PRIORITY_LABELS[priority] || PRIORITY_LABELS[3];

            return (
              <button
                key={entity.entity_id}
                onClick={() => onEntitySelect(entity)}
                className="w-full text-left p-2 rounded-md border border-border hover:border-primary/50 hover:bg-muted/50 transition-colors group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`text-sm ${typeConfig?.color ? 'text-white' : ''}`} style={{ filter: 'none' }}>
                      {TYPE_ICONS[entityType] || '?'}
                    </span>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-foreground truncate">
                        {entity.name}
                      </div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <span>{entityType}</span>
                        <span>|</span>
                        <span>{entity.observation_count || 0} obs</span>
                        {entity.contributing_modules?.length > 0 && (
                          <>
                            <span>|</span>
                            <span>{entity.contributing_modules.length} mod</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0">
                    <span className={`text-[10px] font-bold ${priorityConfig.color}`}>
                      {priorityConfig.label}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {formatTime(entity.last_seen)}
                    </span>
                    {entity.is_watchlisted && (
                      <span className="text-[10px] text-amber-400">WL</span>
                    )}
                  </div>
                </div>
                {entity.last_location && (
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {entity.last_location.lat?.toFixed(4)}, {entity.last_location.lon?.toFixed(4)}
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EntityNavigator;
